import type {
  CompanyApiOrganization,
  GetOrganizationParams,
  GetOrganizationResponse,
  Organization,
  SearchOrganizationParams,
  SearchOrganizationResponse,
  Supplier,
} from '@/types/Organization.ts'
import { apiGetOrNull } from './ApiClient'
import { getSuppliersWithMunicipality } from './CompanyService'
import { getMockOrganization, isMockOrganization } from './MockOrganizationData'

export async function getOrganization(
  params: GetOrganizationParams,
): Promise<GetOrganizationResponse> {
  let company: CompanyApiOrganization | null = null

  // Check if we should use mock data
  if (isMockOrganization(params.orgNumber)) {
    company = getMockOrganization(params.orgNumber)
  } else {
    // Call the real API
    company = await apiGetOrNull<CompanyApiOrganization>(
      `/companies/${encodeURIComponent(params.orgNumber)}`,
    )
  }

  if (!company) {
    return { organization: null }
  }

  // Fetch suppliers for the organization
  let suppliers: Supplier[] = []
  try {
    console.log('Fetching suppliers for organization:', company.organizationNumber)
    const suppliersResponse = await getSuppliersWithMunicipality({
      organizationNumber: company.organizationNumber,
    })
    // Convert DetailedSupplier[] to Supplier[] format
    suppliers = [
      {
        organizationNumber: suppliersResponse.organizationNumber,
        companyName: suppliersResponse.companyName,
        supplierNumbers: suppliersResponse.suppliers.map((s) => parseInt(s.supplierNumber, 10)),
      },
    ]
    console.log('Suppliers fetched:', suppliers)
  } catch (error) {
    // If suppliers endpoint fails, continue with empty suppliers array
    console.warn('Failed to fetch suppliers:', error)
  }

  const organization: Organization = {
    name: company.companyName,
    orgNumber: company.organizationNumber,
    owner: company.owner,
    suppliers,
    address: {
      street: company.businessAddress?.streetNameAndNumber ?? '',
      zipCode: company.businessAddress?.postalCode ?? '',
      city: company.businessAddress?.city ?? '',
      country: company.businessAddress?.country ?? '',
      countryCode: company.businessAddress?.countryCode ?? '',
    },
    postalAddress: {
      street: company.postalAddress?.streetNameAndNumber ?? '',
      zipCode: company.postalAddress?.postalCode ?? '',
      city: company.postalAddress?.city ?? '',
      country: company.postalAddress?.country ?? '',
      countryCode: company.postalAddress?.countryCode ?? '',
    },
  }

  return { organization }
}

export async function searchOrganizations(
  params: SearchOrganizationParams,
): Promise<SearchOrganizationResponse> {
  // Validate that we have a valid search term
  if (!params.searchTerm || params.searchTerm.trim() === '') {
    return {
      organizations: [],
      hasMore: false,
    }
  }

  // For now, the API supports exact lookup by organization number via GET /companies/{organizationNumber}.
  // We treat search as a single-result lookup based on the provided searchTerm (digits only).
  const { organization } = await getOrganization({ orgNumber: params.searchTerm })
  return {
    organizations: organization ? [organization] : [],
    hasMore: false,
  }
}
